import React from 'react';
import { Button, InputBaseComponentProps, Stack, Switch, SxProps, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import {
    mdiAccountCircle,
    mdiBell,
    mdiFileSign,
    mdiShieldAccount,
    mdiStamper,
    mdiViewSequential,
} from '@mdi/js';
import UserAvatar from '../UserAvatar';
import { headingFontBold, vars } from '../../../../Theme';
import { TFunction } from 'react-i18next';
import { urlResetPassword } from '../../../../Constants';
import DocTableDensitySelector from '../../DocTable/DocTableDensitySelector';
import StampSettings from '../../StampSettings/StampSettings';
import { SidepanelVisibilityEnum } from '../../../../redux/user/sliceUserSettings';
import dayjs from 'dayjs';
import SignatureSettings from '../../SignatureSettings/SignatureSettings';

/** Base Stack wrapper styles. */
const switchStackSx: SxProps = {
    mt: 1.5,
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
};

const buttonSx: SxProps = {
    fontFamily: headingFontBold,
    textTransform: 'capitalize',
    backgroundColor: vars.brandGreen,
    color: vars.white,
    height: '32px',
    minWidth: '100px',
    ml: '1rem',
    fontSize: '12px',
    '&:hover': {
        backgroundColor: vars.selectedHoveredGreen,
    },
    '&:disabled': {
        color: vars.white,
        backgroundColor: vars.inactiveGreen,
    },
};

/** Object that provides data for user settings Dialog. */
export const settingsDataObject = ({
    username,
    first_name,
    last_name,
    email,
    last_password_reset,
    dataprotection,
    receiveTasksByEmail,
    isSystemuser,
    pictureLoading,
    sidepanelVisibility,
    headerFilters,
    lastApprovalPasswordChange,
    lastApprovalPasswordChecked,
    handleChangeImage,
    handleChangeSwitch,
    handleChangeSwitchReceiveTasksByEmail,
    handleChangeSwitchSidepanelVisibility,
    handleChangeSwitchHeaderFilters,
    handleChangeSwitchReleasePassword,
    handleApprovalPasswordDialog,
    t,
}: ISettingsDataAttributes): ISettingsDataObject => ({
    profile: {
        name: 'profile',
        icon: mdiAccountCircle,
        data: [
            {
                label: 'userName',
                value: username,
                children: () => (
                    <Stack direction='row' spacing={0.5} sx={{ ml: '1rem', alignItems: 'center' }}>
                        <UserAvatar size={50} loading={pictureLoading} />
                        <Button component='label' variant='outlined' sx={buttonSx}>
                            {t('chooseImage')}
                            <input
                                hidden
                                accept='image/jpg, image/jpeg, image/png'
                                type='file'
                                onChange={handleChangeImage}
                            />
                        </Button>
                    </Stack>
                ),
            },
            {
                label: 'firstName',
                value: first_name,
            },
            {
                label: 'lastName',
                value: last_name,
            },
            {
                label: 'account',
                value: email,
            },
        ],
    },
    security: {
        name: 'security',
        icon: mdiShieldAccount,
        data: [
            {
                label: 'userName',
                value: username,
            },
            {
                label: 'lastPasswordReset',
                value: dayjs(last_password_reset, 'DD/MM/YYYY').format('DD.MM.YYYY'),
                children: () => (
                    <Button component={Link} to={urlResetPassword} sx={buttonSx}>
                        {t('change')}
                    </Button>
                ),
            },
            {
                label: 'privacy',
                value: isSystemuser ? t('isSystemuserText') : t('privacyText'),
                viewItemComponentProps: {
                    align: 'start',
                    multiline: true,
                    inputProps: {
                        sx: {
                            color: vars.midGray,
                            fontSize: '12px',
                        },
                    },
                },
                children: () => (
                    <Switch
                        edge='end'
                        onChange={handleChangeSwitch}
                        checked={dataprotection === '1'}
                        disabled={isSystemuser}
                    />
                ),
            },
            {
                label: 'approvalPassword',
                value: t('approvalPasswordText'),
                viewItemComponentProps: {
                    align: 'start',
                    multiline: true,
                    inputProps: {
                        sx: {
                            color: vars.midGray,
                            fontSize: '12px',
                        },
                    },
                },
                children: () => (
                    <Switch
                        edge='end'
                        onChange={handleChangeSwitchReleasePassword}
                        checked={!!+lastApprovalPasswordChecked}
                    />
                ),
            },
            {
                label: 'lastApprovalPasswordChange',
                value: lastApprovalPasswordChange,
                children: () => (
                    <Button
                        onClick={handleApprovalPasswordDialog}
                        disabled={!+lastApprovalPasswordChecked}
                        sx={buttonSx}
                    >
                        {t('change')}
                    </Button>
                ),
            },
        ],
    },
    layout: {
        name: 'layout',
        icon: mdiViewSequential,
        data: [
            {
                label: 'layout',
                hideLabel: true,
                value: t('gridDensity'),
                viewItemComponentProps: {
                    align: 'start',
                    multiline: false,
                    direction: 'column',
                },
                children: () => <DocTableDensitySelector />,
            },
            {
                label: 'sidepanelVisibility',
                hideLabel: true,
                value: t('sidepanelVisibility'),
                viewItemComponentProps: {
                    align: 'start',
                    multiline: true,
                    direction: 'column',
                },
                children: () => (
                    <Stack direction='row' sx={switchStackSx}>
                        <Typography>{t('expandedSidepanel')}</Typography>
                        <Switch
                            edge='end'
                            onChange={handleChangeSwitchSidepanelVisibility}
                            checked={sidepanelVisibility === SidepanelVisibilityEnum.expanded}
                        />
                    </Stack>
                ),
            },
            {
                label: 'headerFiltersVisibility',
                hideLabel: true,
                value: t('headerFiltersVisibility'),
                viewItemComponentProps: {
                    align: 'start',
                    multiline: true,
                    direction: 'column',
                },
                children: () => (
                    <Stack direction='row' sx={switchStackSx}>
                        <Typography>{t('expandedHeaderFilters')}</Typography>
                        <Switch
                            edge='end'
                            onChange={handleChangeSwitchHeaderFilters}
                            checked={headerFilters}
                        />
                    </Stack>
                ),
            },
        ],
    },
    stamps: {
        name: 'stamps',
        icon: mdiStamper,
        data: [
            {
                label: 'stamps',
                value: '',
                viewItemComponentProps: {
                    align: 'start',
                    multiline: false,
                    direction: 'column',
                },
                children: () => <StampSettings />,
            },
        ],
    },
    notifications: {
        name: 'notifications',
        icon: mdiBell,
        data: [
            {
                label: 'notifications',
                hideLabel: true,
                value: t('notificationsReceiveTasksByEmail'),
                viewItemComponentProps: {
                    align: 'start',
                    multiline: true,
                },

                children: () => (
                    <Switch
                        edge='end'
                        onChange={handleChangeSwitchReceiveTasksByEmail}
                        checked={receiveTasksByEmail}
                    />
                ),
            },
        ],
    },
    signature: {
        name: 'signature',
        icon: mdiFileSign,
        data: [
            {
                label: 'signature',
                value: '',
                hideLabel: true,
                viewItemComponentProps: {
                    align: 'start',
                    multiline: false,
                    direction: 'column',
                },
                children: () => <SignatureSettings />,
            },
        ],
    },
});

/**
 * Interface that describes Settings Dialog Tab's Data.
 */
export interface ISettingsDialogTabData {
    label: string;
    hideLabel?: boolean;
    value: string;
    viewItemComponentProps?: {
        align?: string;
        multiline?: boolean;
        direction?: string;
        inputProps?: InputBaseComponentProps;
    };

    children?: () => JSX.Element;
}

/**
 * Interface that describes Setting's Dialog Tab.
 */
interface ISettingsDialogTab {
    name: string;
    icon: string;
    data: ISettingsDialogTabData[];
}

/**
 * Interface that describes Setting's Dialog Data Object.
 */
export interface ISettingsDataObject {
    profile: ISettingsDialogTab;
    security: ISettingsDialogTab;
    layout: ISettingsDialogTab;
    stamps: ISettingsDialogTab;
    notifications: ISettingsDialogTab;
    signature: ISettingsDialogTab;
}

/**
 * Interface that describes Setting's Dialog Data Attributes.
 */
export interface ISettingsDataAttributes {
    username: string;
    first_name: string;
    last_name: string;
    email: string;
    last_password_reset: string;
    dataprotection: string;
    receiveTasksByEmail: boolean;
    isSystemuser: boolean;
    pictureLoading: boolean;
    sidepanelVisibility: SidepanelVisibilityEnum;
    headerFilters: boolean;
    lastApprovalPasswordChange: string;
    lastApprovalPasswordChecked: boolean;
    handleChangeImage: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleChangeSwitch: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleChangeSwitchReceiveTasksByEmail: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleChangeSwitchSidepanelVisibility: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleChangeSwitchHeaderFilters: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleChangeSwitchReleasePassword: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleApprovalPasswordDialog: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    t: TFunction<'header', undefined>;
}
